import { Project, ProjectDetailsFieldType, ProjectType } from './Types';
import { useBackendApi } from './backendApi';
import { useEventsPublisher, useEventsSubscriber } from 'event-bus';
import { useCacheService } from './cacheService';
import { PROJECT_DATES_FIELDS_UPDATED } from './projectDatesFieldsService';
import { useSetupAreaIdContext } from './SetupAreaIdContext';
import { PROJECT_COMMISSION_FIELD_UPDATED } from "./projectCommissionFieldService";
import { PROJECT_CLOSING_DATE_FIELD_UPDATED } from "./projectClosingDateFieldService";

export enum ProjectDetailsFieldPermission {
  DELETE = 'DELETE',
}

export interface ProjectDetailsField {
  id: number
  name: string
  originalName?: string
  fieldType: ProjectDetailsFieldType
  order: number,
  prompt: string,
  isHidden: boolean
  isPreexisting: boolean
  isProjectStartDate: boolean
  isProjectEndDate: boolean
  isExcludedFromSet: boolean
  isCommissionField: boolean
  isClosingDateField: boolean
  parentFieldId?: number,
  _parentField?: {
    id: number,
    name: string,
    fieldType: ProjectDetailsFieldType,
    isProjectStartDate: boolean,
    isProjectEndDate: boolean,
  },
  projectTypes: ProjectType[],
  permissions: ProjectDetailsFieldPermission[],
  groupId?: number,
  _setupArea?: {
    id: number,
    name: string,
  }
}

export interface CreateProjectDetailsFieldRequest {
  name: string;
  fieldType: ProjectDetailsFieldType;
  order: number;
}

export interface UpdateProjectDetailsFieldRequest {
  name: string,
  prompt?: string,
  alias: string
}

export interface UpdatePredefinedFieldRequest {
  name?: string,
  isHidden: boolean,
  prompt?: string,
  fieldType: ProjectDetailsFieldType,
  [key: string]: any
}

export interface SetHiddenRequest {
  isHidden: boolean,

  [key: string]: any
}

export const PROJECT_DETAILS_FIELDS_UPDATED = 'PROJECT_DETAILS_FIELDS_UPDATED';

export const PROJECT_DETAILS_FIELD_ORDER_UPDATED = 'PROJECT_DETAILS_FIELDS_ORDER_UPDATES';

export function useProjectDetailsFieldsService() {
  const nekstApi = useBackendApi();
  const eventsPublisher = useEventsPublisher();

  const cacheService = useCacheService('ProjectDetailsFieldsService');

  const setupAreaContext = useSetupAreaIdContext();

  function getSetupAreaId(setupAreaId?: number | string) {
    return setupAreaId || setupAreaContext?.setupAreaId || 'internal';
  }

  function getSetupAreaQueryParam(setupAreaId?: number | string) {
    const id = getSetupAreaId(setupAreaId);
    return id ? `setupAreaId=${id}` : '';
  }

  function clearProjectTypeCache(projectType: ProjectType) {
    cacheService.clearByPrefix(`fields-${projectType}`);
  }

  useEventsSubscriber(
    'ProjectDetailsFieldsService',
    {
      [PROJECT_DETAILS_FIELDS_UPDATED]: (eventData) => {
        clearProjectTypeCache(eventData.projectType);
      },
      [PROJECT_DETAILS_FIELD_ORDER_UPDATED]: (eventData) => {
        clearProjectTypeCache(eventData.projectType);
      },
      [PROJECT_DATES_FIELDS_UPDATED]: (eventData) => {
        clearProjectTypeCache(eventData.projectType);
      },
      [PROJECT_COMMISSION_FIELD_UPDATED]: (eventData) => {
        clearProjectTypeCache(eventData.projectType);
      },
      [PROJECT_CLOSING_DATE_FIELD_UPDATED]: (eventData) => {
        clearProjectTypeCache(eventData.projectType);
      }
    },
  );

  const getFields = async (
    projectType: ProjectType,
    returnHidden = false,
    setupAreaId?: number | string
  ) => {

    const result = await cacheService.get<ProjectDetailsField[]>(`fields-${projectType}-${getSetupAreaId(setupAreaId)}-0`, async (cacheItem) => {
      cacheItem.setExpireInSeconds(60);
      return await nekstApi.get(`/settings/projects/${projectType.toLowerCase()}/detailfields?${getSetupAreaQueryParam(setupAreaId)}`) as ProjectDetailsField[];
    });

    if (returnHidden) {
      return result;
    } else {
      return result.filter((item) => !item.isHidden);
    }
  };

  const getAllSetupAreasFields = async (
    projectType: ProjectType,
  ) => {
    const result = await cacheService.get<ProjectDetailsField[]>(`fields-${projectType}-setupareas`, async (cacheItem) => {
      cacheItem.setExpireInSeconds(180);
      return await nekstApi.get(`/settings/projects/${projectType.toLowerCase()}/allsetupareasfields`) as ProjectDetailsField[];
    });

    return result.filter((item) => !item.isHidden);
  };

  const createField = async (
    projectType: ProjectType,
    request: CreateProjectDetailsFieldRequest,
    setupAreaId?: number | string
  ) => {
    const result = await nekstApi.post(`/settings/projects/${projectType.toLowerCase()}/detailfields?${getSetupAreaQueryParam(setupAreaId)}`, request) as ProjectDetailsField;

    eventsPublisher.publish(PROJECT_DETAILS_FIELDS_UPDATED, {
      projectType,
    });
    return result;
  };

  const updateField = async (
    projectType: ProjectType,
    fieldId: number,
    request: UpdateProjectDetailsFieldRequest,
    setupAreaId?: number | string
  ) => {
    const result = await nekstApi.put(`/settings/projects/${projectType.toLowerCase()}/detailfields/${fieldId}?${getSetupAreaQueryParam(setupAreaId)}`, request) as ProjectDetailsField;

    eventsPublisher.publish(PROJECT_DETAILS_FIELDS_UPDATED, {
      projectType,
    });
    return result;
  };

  const updatePredefinedField = async (
    projectType: ProjectType,
    fieldId: number,
    request: UpdatePredefinedFieldRequest,
    setupAreaId?: number
  ) => {
    const result = await nekstApi.put(`/settings/projects/${projectType.toLowerCase()}/detailfields/global/${fieldId}?${getSetupAreaQueryParam(setupAreaId)}`, request) as ProjectDetailsField;

    eventsPublisher.publish(PROJECT_DETAILS_FIELDS_UPDATED, {
      projectType,
    });
    return result;
  };

  const setHidden = async (
    projectType: ProjectType,
    fieldId: number,
    request: SetHiddenRequest,
    setupAreaId?: number
  ) => {
    const result = await nekstApi.put(`/settings/projects/${projectType.toLowerCase()}/detailfields/${fieldId}/hidden?${getSetupAreaQueryParam(setupAreaId)}`, request) as ProjectDetailsField;

    eventsPublisher.publish(PROJECT_DETAILS_FIELDS_UPDATED, {
      projectType,
    });

    return result;
  };

  const deleteField = async (
    projectType: ProjectType,
    fieldId: number,
    setupAreaId?: number
  ) => {
    const result = await nekstApi.delete(`/settings/projects/${projectType.toLowerCase()}/detailfields/${fieldId}?${getSetupAreaQueryParam(setupAreaId)}`) as void;

    eventsPublisher.publish(PROJECT_DETAILS_FIELDS_UPDATED, {
      projectType,
    });

    return result;
  };

  const getFieldUsages = async (
    fieldId: number,
  ) => {
    return await nekstApi.get(`/projects/details/fields/${fieldId}/usages`) as Project[];
  };

  const setOrder = async (
    projectType: ProjectType,
    ids: number[],
    setupAreaId?: number,
  ) => {
    const result = await nekstApi.put(`/settings/projects/${projectType}/detailfields/bulk/order?${getSetupAreaQueryParam(setupAreaId)}`, {
      ids,
    }) as ProjectDetailsField[];

    eventsPublisher.publish(PROJECT_DETAILS_FIELD_ORDER_UPDATED, {
      projectType,
    });
    return result;
  };

  const getFieldsInSet = async (
    setId: number,
    returnHidden = false
  ) => {
    return await nekstApi.get(`/detailsfields/sets/${setId}/fields?returnHidden=${returnHidden}`) as ProjectDetailsField[];
  }

  const __clearCache = () => {
    // clear cache for all project types
    Object.values(ProjectType)
      .forEach((projectType) => {
        clearProjectTypeCache(projectType);
      });
  };

  const getFieldsApplicableForPlan = async (
    planId: number
  ) => {
    return await nekstApi.get(`/settings/projects/detailsfields/forplan/${planId}`) as ProjectDetailsField[];
  };

  const getFieldsApplicableForContractTemplate = async (templateId: number) => {
    return await nekstApi.get(`/settings/projects/detailsfields/forcontracttemplate/${templateId}`) as ProjectDetailsField[];
  }


  return {
    getFields,
    createField,
    updateField,
    updatePredefinedField,
    getAllSetupAreasFields,
    setHidden,
    deleteField,
    getFieldUsages,
    setOrder,
    getFieldsInSet,
    getFieldsApplicableForPlan,
    getFieldsApplicableForContractTemplate,
    __clearCache,
  };
}
